import { getStorageItem, getStorageObject, removeStorageItem, setStorageItem, setStorageObject } from './local-storage';

/**
 * Event-scoped storage for the live-attendee registration token + user-code.
 *
 * BACKGROUND
 * The attendee registration JWT has historically lived under GLOBAL localStorage keys
 * ('bl-profile', 'Bl-Profile-Token') plus a global 'user-code'. Sibling events in a
 * series are served on a single shared origin (REACT_APP_REDIRECT_HOST/{uuid}), so two
 * tabs on different events in the same series read/write the SAME global keys and clobber
 * each other — and removing a global key triggers a cross-tab logout cascade
 * (see use-clear-storage-redirect.ts -> deregister.ts). The per-event `reg.${uuid}` key is
 * already scoped; the token/user-code are not.
 *
 * These helpers introduce EVENT-SCOPED keys so each tab owns its own per-event token.
 *
 * HOW IT WORKS:
 *  - Reads prefer the event-scoped key and fall back to the legacy global key.
 *  - Writes/removes always update the event-scoped key, and — while WRITE_LEGACY_GLOBAL is
 *    true — also mirror the legacy global key.
 *
 * Tab isolation does NOT depend on WRITE_LEGACY_GLOBAL. It holds because every reader goes
 * through getAttendeeToken/getAttendeeUserCode(eventUuid) (which prefer the scoped key) and
 * use-clear-storage-redirect.ts watches the event-scoped key, not the globals. The flag only
 * controls whether the legacy global keys are ALSO kept in sync as a fallback (see below).
 */

// Legacy GLOBAL keys (pre-event-scoping). Exported so the cross-tab listener and any
// transitional code can reference the exact strings.
export const LEGACY_TOKEN_KEY = 'Bl-Profile-Token';
export const LEGACY_PROFILE_KEY = 'bl-profile';
export const LEGACY_USER_CODE_KEY = 'user-code';

// Controls whether writes/removes ALSO mirror the legacy GLOBAL keys, on top of the
// event-scoped keys. Kept `true` so anything still reading the legacy globals (e.g. an older
// session, or code outside this module) keeps working; tab isolation does not depend on it.
// Can optionally be flipped to `false` to fully decouple event-scoped storage from the legacy
// globals once nothing relies on them.
const WRITE_LEGACY_GLOBAL = true;

// TTL (hours) for the event-scoped attendee keys so they don't accumulate in localStorage
// forever. Without it, every event an attendee touches leaves token/profile/user-code keys
// behind with no eviction — they're only cleared on an explicit sign-out (clearStorage). The
// storage helpers already evict lazily on read once `__expiration` passes (local-storage.ts
// getStorageItem/getStorageObject), so no sweep is needed. 90 days is shorter than the backend
// JWT's 365-day exp, so a returning attendee past the window simply re-validates/registers via
// the normal flow. NOTE: a boot-time sweep is deliberately avoided — proactively removing a
// sibling event's key would fire the cross-tab `storage` listener and re-trigger the logout
// cascade that event-scoping exists to prevent (see use-clear-storage-redirect.ts).
const ATTENDEE_TOKEN_TTL_HOURS = 24 * 90;

const tokenKey = (eventUuid: string) => `${LEGACY_TOKEN_KEY}.${eventUuid}`;
const profileKey = (eventUuid: string) => `${LEGACY_PROFILE_KEY}.${eventUuid}`;
const userCodeKey = (eventUuid: string) => `${LEGACY_USER_CODE_KEY}.${eventUuid}`;

/**
 * Read the attendee registration token for an event. Prefers the event-scoped key and
 * falls back to the legacy global key (read-through during migration). Pass no eventUuid
 * to read only the legacy global key.
 */
export function getAttendeeToken(eventUuid?: string | null): string | null {
	if (eventUuid) {
		const scoped = getStorageItem(tokenKey(eventUuid));
		if (scoped) return scoped;
	}
	return getStorageItem(LEGACY_TOKEN_KEY);
}

/**
 * Persist the attendee registration token for an event. Writes both event-scoped token
 * keys ('Bl-Profile-Token.${uuid}' and 'bl-profile.${uuid}', kept in sync as the legacy
 * pair was) and — during migration — the legacy global keys.
 */
export function setAttendeeToken(eventUuid: string, token: string): void {
	setStorageItem(tokenKey(eventUuid), token, ATTENDEE_TOKEN_TTL_HOURS);
	setStorageItem(profileKey(eventUuid), token, ATTENDEE_TOKEN_TTL_HOURS);

	if (WRITE_LEGACY_GLOBAL) {
		setStorageItem(LEGACY_PROFILE_KEY, token, ATTENDEE_TOKEN_TTL_HOURS);
		setStorageItem(LEGACY_TOKEN_KEY, token, ATTENDEE_TOKEN_TTL_HOURS);
	}
}

/** Remove the attendee registration token for an event (both scoped keys, plus legacy globals during migration). */
export function removeAttendeeToken(eventUuid?: string | null): void {
	if (eventUuid) {
		removeStorageItem(tokenKey(eventUuid));
		removeStorageItem(profileKey(eventUuid));
	}

	if (WRITE_LEGACY_GLOBAL) {
		removeStorageItem(LEGACY_PROFILE_KEY);
		removeStorageItem(LEGACY_TOKEN_KEY);
	}
}

/** Read the email-verification user-code for an event (scoped, falling back to legacy global). */
export function getAttendeeUserCode(eventUuid?: string | null): string | null {
	if (eventUuid) {
		const scoped = getStorageItem(userCodeKey(eventUuid));
		if (scoped) return scoped;
	}
	return getStorageItem(LEGACY_USER_CODE_KEY);
}

/** Persist the email-verification user-code for an event (scoped, plus legacy global during migration). */
export function setAttendeeUserCode(eventUuid: string, userCode: string): void {
	setStorageItem(userCodeKey(eventUuid), userCode, ATTENDEE_TOKEN_TTL_HOURS);

	if (WRITE_LEGACY_GLOBAL) {
		setStorageItem(LEGACY_USER_CODE_KEY, userCode, ATTENDEE_TOKEN_TTL_HOURS);
	}
}

/** Remove the email-verification user-code for an event (scoped, plus legacy global during migration). */
export function removeAttendeeUserCode(eventUuid?: string | null): void {
	if (eventUuid) {
		removeStorageItem(userCodeKey(eventUuid));
	}

	if (WRITE_LEGACY_GLOBAL) {
		removeStorageItem(LEGACY_USER_CODE_KEY);
	}
}

/**
 * Series "seed": a read-only bootstrap identity for this origin, written whenever ANY event
 * validates/registers. When an attendee lands on a sibling series event they have not yet
 * registered for on this device (no event-scoped token), the series auto-register page-load
 * branch reads this token to identify them to the backend, which resolves series membership
 * and eligibility itself. It is bootstrap input only — never the live session token.
 *
 * A single key suffices: the token identifies the person (bl_profile), and the backend does
 * the per-series eligibility check. It is forward-compat for when WRITE_LEGACY_GLOBAL is
 * flipped off and the legacy global token is no longer available as a fallback.
 */
const SERIES_SEED_KEY = 'bl-series-token';

export interface SeriesSeed {
	token: string;
	email?: string;
}

export function getSeriesSeed(): SeriesSeed | null {
	return getStorageObject<SeriesSeed>(SERIES_SEED_KEY);
}

export function setSeriesSeed(seed: SeriesSeed): void {
	setStorageObject(SERIES_SEED_KEY, seed, ATTENDEE_TOKEN_TTL_HOURS);
}

export function removeSeriesSeed(): void {
	removeStorageItem(SERIES_SEED_KEY);
}
